// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Material
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';

// Local library
import {
  MatNotificationService,
  UnsubscribeObservableService,
  TranslatePipe
} from '@amwager/util';
import { CommonFacade, ENVIRONMENT, SessionService } from '@amwager/data-access';
import { FundingService } from '@amwager/funding/data-access';
import { AuthFacade } from '@amwager/authentication/data-access';

// Local file
import { DebitCreditCardComponent } from '../deposit/debit-credit-card/debit-credit-card.component';
import { PromoCodeComponent } from '../deposit/promo-code/promo-code.component';
import { MoneyPakComponent } from '../deposit/money-pak/money-pak.component';
import { PayNearMeComponent } from '../deposit/pay-near-me/pay-near-me.component';
import { WireTransferComponent } from '../deposit/wire-transfer/wire-transfer.component';
import { CryptoCurrencyComponent } from '../deposit/crypto-currency/crypto-currency.component';
import { OnlineCheckComponent } from '../deposit/online-check/online-check.component';

import {
  DebitCreditCardComponent as WithdrawDebitCreditCardComponent
} from '../withdraw/debit-credit-card/debit-credit-card.component';
import {
  WireTransferComponent as WithdrawWireTransferComponent
} from '../withdraw/wire-transfer/wire-transfer.component';
import {
  CheckByEmailComponent as WithdrawCheckByEmailComponent
} from '../withdraw/check-by-email/check-by-email.component';

import {
  OnlineCheckComponent as WithdrawOnlineCheckComponent
} from '../withdraw/online-check/online-check.component';
import { HeldComponent } from '../held/held.component';

/** Tabs rendered in the funding heading bar. */
export type TFundingTab = 'deposit' | 'withdraw' | 'held';

/**
 * A payment method row. `label` is the English copy the `labelKey` falls back
 * to, and `audience` restricts the row to a single affiliate.
 */
export interface IFundingMethod {
  id: string;
  labelKey: string;
  label: string;
  logo: string;
  dataCy?: string;
  labelDataCy?: string;
  audience?: 'nhplay' | 'non-nhplay';
}

@Component({
  selector: 'feature-mat-funding',
  templateUrl: './funding.component.html',
  styleUrls: ['./funding.component.scss'],
  imports: [
    MatIconModule,
    CommonModule,
    DebitCreditCardComponent,
    PromoCodeComponent,
    MoneyPakComponent,
    PayNearMeComponent,
    WireTransferComponent,
    CryptoCurrencyComponent,
    OnlineCheckComponent,
    WithdrawDebitCreditCardComponent,
    WithdrawWireTransferComponent,
    WithdrawCheckByEmailComponent,
    WithdrawOnlineCheckComponent,
    HeldComponent,
    TranslatePipe
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FundingComponent extends UnsubscribeObservableService
  implements OnInit {

  @ViewChild(PayNearMeComponent) payNearMeComponent!: PayNearMeComponent;

  // Emitted when the funding view is dismissed (embedded host can react).
  @Output() closed = new EventEmitter<void>();

  readonly tabs: { id: TFundingTab; labelKey: string; label: string; dataCy: string }[] = [
    {
      id: 'deposit',
      labelKey: 'account.funding.deposit.credit.depo',
      label: 'Deposit',
      dataCy: 'funding-deposit-header'
    },
    {
      id: 'withdraw',
      labelKey: 'ui.funding.withdraw',
      label: 'Withdraw',
      dataCy: 'funding-withdraw-header'
    },
    {
      id: 'held',
      labelKey: 'ui.funding.held',
      label: 'Held',
      dataCy: 'funding-held-header'
    }
  ];

  readonly depositMethods: IFundingMethod[] = [
    {
      id: 'debitAndCreditCard',
      labelKey: 'account.funding.deposit.credit.header',
      label: 'Debit and Credit Card',
      logo: '/assets/images/funding/visa_mastercard_logo.png',
      dataCy: 'dep-card-cont',
      labelDataCy: 'funding-deposit-card'
    },
    {
      id: 'cryptoCurrency',
      labelKey: 'account.funding.deposit.bitcoin',
      label: 'Crypto Currency',
      logo: '/assets/images/funding/crypto_currency_logo.png',
      dataCy: 'dep-crypto-cont',
      labelDataCy: 'funding-deposit-crypto'
    },
    {
      id: 'onlineCheck',
      labelKey: 'account.funding.deposit.ach',
      label: 'Online Check (ACH)',
      logo: '/assets/images/funding/ach_logo.png',
      dataCy: 'dep-ach-cont',
      labelDataCy: 'funding-deposit-ach',
      audience: 'non-nhplay'
    },
    {
      id: 'nhPlay',
      labelKey: 'ui.funding.nh.play.account',
      label: 'NH Play Account',
      logo: '/assets/images/funding/nh_play_logo.png',
      labelDataCy: 'funding-deposit-ach',
      audience: 'nhplay'
    },
    {
      id: 'payNearMe',
      labelKey: 'account.funding.deposit.paynearme',
      label: 'PayNearMe',
      logo: '/assets/images/funding/pay_near_me_logo.png',
      dataCy: 'dep-paynearme-cont',
      labelDataCy: 'funding-deposit-paynearme'
    },
    {
      id: 'moneyPak',
      labelKey: 'account.fudning.deposit.moneypak',
      label: 'MoneyPak',
      logo: '/assets/images/funding/money_pak_logo.png',
      dataCy: 'dep-moneypak-cont',
      labelDataCy: 'funding-deposit-moneypak'
    },
    {
      id: 'wireTransfer',
      labelKey: 'account.funding.wire',
      label: 'Wire Transfer',
      logo: '/assets/images/funding/wire_transfer_logo.png',
      dataCy: 'dep-wire-cont',
      labelDataCy: 'funding-deposit-wire'
    },
    {
      id: 'checkMoneyOrder',
      labelKey: 'account.funding.deposit.check',
      label: 'Check / Money Order',
      logo: '/assets/images/funding/check_money_order_logo.png',
      dataCy: 'dep-moneyOrder-cont',
      labelDataCy: 'funding-deposit-money-order'
    },
    {
      id: 'promoCode',
      labelKey: 'account.funding.deposit.promocode.heading',
      label: 'Promo Code',
      logo: '/assets/images/funding/promo_code_logo.png',
      dataCy: 'funding-deposit-promo-code'
    }
  ];

  readonly withdrawMethods: IFundingMethod[] = [
    {
      id: 'debitAndCreditCard',
      labelKey: 'account.funding.deposit.credit.header',
      label: 'Debit and Credit Card',
      logo: '/assets/images/funding/visa_mastercard_logo.png',
      dataCy: 'wdraw-card-cont',
      labelDataCy: 'funding-withdraw-card'
    },
    {
      id: 'onlineCheck',
      labelKey: 'account.funding.deposit.ach',
      label: 'Online Check (ACH)',
      logo: '/assets/images/funding/ach_logo.png',
      dataCy: 'wdraw-ach-cont',
      labelDataCy: 'funding-deposit-ach'
    },
    {
      id: 'checkByEmail',
      labelKey: 'account.funding.withdraw.check',
      label: 'Check By Mail',
      logo: '/assets/images/funding/check_money_order_logo.png',
      dataCy: 'wdraw-chkByMail-cont',
      labelDataCy: 'funding-withdraw-check-by-mail'
    },
    {
      id: 'wireTransfer',
      labelKey: 'account.funding.wire',
      label: 'Wire Transfer',
      logo: '/assets/images/funding/wire_transfer_logo.png',
      dataCy: 'wdraw-wire-cont',
      labelDataCy: 'funding-withdraw-wire'
    }
  ];

  activeTab: TFundingTab = 'deposit';

  /** Payment method whose card is expanded; empty when the list is collapsed. */
  methodType: string = '';

  affiliateName: string = '';
  isOnline: boolean = true;

  constructor(
    private readonly matNotificationService: MatNotificationService,
    private readonly sessionService: SessionService,
    private readonly commonFacade: CommonFacade,
    private readonly authFacade: AuthFacade,
    private readonly cdRef: ChangeDetectorRef,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) readonly environment: string,
    @Optional() private readonly dialogRef?: MatDialogRef<FundingComponent>,
  ) {
    super();

    this.authFacade.isLogoutSuccess$().subscribe((isLogout?: boolean) => {
      if (isLogout) {
        this.dialogRef?.close();
        this.closed.emit();
        this.matNotificationService.hide();
      }
    });
  }

  ngOnInit() {
    this.checkConnectivity();
    this.affiliateName = this.sessionService.getAffiliateName();
  }

  get isNhPlay(): boolean {
    return (this.affiliateName || '').toLowerCase() === 'nhplay';
  }

  /** Withdrawals — and the held list that belongs to them — are not offered to NHPlay. */
  isTabHidden(tab: TFundingTab): boolean {
    return tab !== 'deposit' && this.isNhPlay;
  }

  selectTab(tab: TFundingTab): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:selectTab', false);
    }

    if (this.isTabHidden(tab)) return;

    this.activeTab = tab;

    // Leaving a tab collapses whatever payment method was expanded.
    this.methodType = '';
    this.cdRef.markForCheck();
  }

  isMethodAvailable(method: IFundingMethod): boolean {
    if (method.audience === 'nhplay') return this.isNhPlay;
    if (method.audience === 'non-nhplay') return !this.isNhPlay;

    return true;
  }

  /** Once a method is expanded the remaining rows step aside. */
  isRowHidden(method: string): boolean {
    return !!this.methodType && this.methodType !== method;
  }

  trackByMethodId(index: number, method: IFundingMethod): string {
    return method.id;
  }

  selectCard(method: string): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:selectCard', false);
    }

    if (method === 'checkMoneyOrder') {
      this.showAlertInfo(
        "We're working hard to bring this feature to you! Stay tuned for updates."
      );
      return;
    }

    if (method === 'payNearMe') {
      this.payNearMe();
      return;
    }

    if (method === 'nhPlay') {
      this.showAlertMessage(
        'NHPlay Redirect',
        'NHPlay now requires all users to sign in directly to their site.' +
        'We appologize for the inconvience. Please clicl "Continue" to go to NHPlay.com'
      );
      return;
    }

    // Clicking the open row collapses it back to the full list.
    this.methodType = this.methodType === method ? '' : method;
    this.cdRef.markForCheck();
  }

  payNearMe(): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('FundingComponent:payNearMe', false);
      return;
    }

    this.payNearMeComponent.openLink();
  }

  showAlertMessage(hdr: string, msg: string): void {
    this.matNotificationService.showAlert(hdr, msg, undefined, 'Continue', 'Cancel')
      .subscribe((isContinue: boolean) => {
        if (isContinue) {
          window.open('https://nhplay.com/login.cfm', '_blank');
        }
      });
  }

  cancel(): void {
    if (this.methodType) {
      this.methodType = '';
      this.cdRef.markForCheck();
      return;
    }

    this.close();
  }

  close(): void {
    this.closed.emit();
    this.dialogRef?.close();
  }

  showAlertInfo(message: string): void {
    this.matNotificationService.showAlert('Info', message, undefined, undefined, 'Close');
  }

  checkConnectivity(): void {
    this.fundingService.checkConnectivity(
      this.environment,
      'FundingComponent:checkConnectivity',
      (online: boolean) => this.isOnline = online
    );
  }

}
